import type { InjectedRouter } from 'react-router';

import { BannerPlacement } from '../../../../typings/enums';
import { window } from '../../../globals';
import BannerManager from '../../BannerManager';
import { Link } from '../../generic';
import { FormGroup } from '../../generic/Forms';
import FOSSALogoWithCatWhite from '../../generic/FOSSAInlineSVG/FOSSALogoWithCatWhite';
import { icons } from '../../generic/Icon/icons';
import ForgotPasswordLink from '../common/ForgotPasswordLink';
import SignInSignUpToggle from '../common/SignInSignUpToggle';
import AuthContextProvider, { useAuthContext } from '../context/AuthContext';
import AuthForm from '../form';
import MfaVerification from '../form/MfaVerification';
import OrgPicker from '../form/OrgPicker';
import CenteredLayout from '../layouts/Centered';
import type { OrganizationSummary } from '../../../../shared/organizationV2';
import type { RouterProps } from '../../../types';
import type { LoginStep } from '../context/AuthContext';
import type { SubtitleAction } from '../form/Heading';

/**
 * Page for logging into FOSSA.
 *
 * AuthContext manages the state for logging in, this is just the markup for that page.
 */
const getHeading = (step: string): string => {
  if (step === 'verifyMfa') {
    return 'Two-Factor Authentication';
  }
  if (step === 'selectOrg') {
    return 'Choose an organization';
  }
  return 'Sign In';
};

type HeadingContext = { subtitle?: string; action?: SubtitleAction };

/**
 * Subtitle + edit affordance for the user's last choice:
 * entered email on the picker
 * selected org on the password step.
 */
const getHeadingContext = ({
  step,
  emailValue,
  organizations,
  selectedOrgId,
  onResetOrgSelection,
  onReturnToOrgPicker,
}: {
  step: LoginStep;
  emailValue: string;
  organizations: OrganizationSummary[];
  selectedOrgId: number | null;
  onResetOrgSelection: () => void;
  onReturnToOrgPicker: () => void;
}): HeadingContext => {
  if (step === 'selectOrg') {
    return {
      subtitle: emailValue || undefined,
      action: { icon: icons.pencil, label: 'Use a different email', onClick: onResetOrgSelection },
    };
  }
  if (step === 'authenticate' && organizations.length > 1) {
    return {
      subtitle: organizations.find(o => o.id === selectedOrgId)?.title,
      action: { icon: icons.pencil, label: 'Change organization', onClick: onReturnToOrgPicker },
    };
  }
  return {};
};

const Login = () => {
  const {
    email: {
      ref: emailInputRef,
      ...email
    },
    password: {
      ref: passwordInputRef,
      ...password
    },
    alert,
    status,
    passwordStrength,
    hidePasswordInput,
    isLdapAuthRequired,
    step,
    page,
    submitButton,
    loginMethods,
    inviteToken,
    samlURL,
    hasInvite,
    organizations,
    selectedOrgId,
    onSelectOrg,
    onResetOrgSelection,
    onReturnToOrgPicker,
    next: nextUrl,
  } = useAuthContext();
  const isEmailPasswordStep = step !== 'verifyMfa' && step !== 'selectOrg';
  const { subtitle: headingSubtitle, action: subtitleAction } = getHeadingContext({
    step,
    emailValue: email.value,
    organizations,
    selectedOrgId,
    onResetOrgSelection,
    onReturnToOrgPicker,
  });
  return (
    <CenteredLayout>
      <div className="fossa-auth__layout__logo">
        <Link to="https://fossa.com/" openInNewTab>
          <FOSSALogoWithCatWhite width={150} />
        </Link>
      </div>
      <AuthForm.Container onSubmit={submitButton.onSubmit}>
        <AuthForm.Heading heading={getHeading(step)} orgTitle={headingSubtitle} subtitleAction={subtitleAction} />
        <AuthForm.Alert alert={alert} status={status} />
        {step === 'verifyMfa' && <MfaVerification />}
        {step === 'selectOrg' && (
          <OrgPicker
            organizations={organizations}
            selectedOrgId={selectedOrgId}
            onSelectOrg={onSelectOrg}
          />
        )}
        {isEmailPasswordStep && (
          <>
            <FormGroup>
              <AuthForm.EmailInput
                ref={emailInputRef}
                email={email}
                hidePasswordInput={hidePasswordInput}
                step={step}
                onSubmit={submitButton.onSubmit}
                hasInvite={hasInvite}
              />
              <AuthForm.PasswordInput
                ref={passwordInputRef}
                step={step}
                password={password}
                hidePasswordInput={hidePasswordInput}
                passwordStrength={passwordStrength}
                onSubmit={submitButton.onSubmit}
              />
              {!isLdapAuthRequired && <ForgotPasswordLink />}
            </FormGroup>
            {samlURL && <AuthForm.SamlLoginButton samlURL={samlURL} />}
          </>
        )}
        <AuthForm.SubmitButton
          page={page}
          step={step}
          status={status}
          submitButton={submitButton}
          hidePasswordInput={hidePasswordInput}
        />
        {isEmailPasswordStep && (
          <AuthForm.LoginMethods
            loginMethods={loginMethods}
            hidePasswordInput={hidePasswordInput}
            inviteToken={inviteToken}
            page={page}
          />
        )}
      </AuthForm.Container>
      <SignInSignUpToggle page="login" email={email.value} next={nextUrl} />
      {window.on_prem && <BannerManager placement={BannerPlacement.Login} />}
    </CenteredLayout>
  );
};

type LoginPageProps = {
  location: RouterProps['location'];
  router: InjectedRouter;
};

/**
 * Login page for Auth 2.0
 */
const LoginPage = ({ location, router }: LoginPageProps) => (
  <AuthContextProvider location={location} router={router}>
    <Login />
  </AuthContextProvider>
);

export default LoginPage;
